import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Calendar, Users, Heart, XCircle, Sparkles, ArrowRight } from "lucide-react";

const WunderlistHistory = () => {
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Wunderlist History: The Beloved To-Do App That Microsoft Shut Down",
    "description": "The complete history of Wunderlist, from its 2011 launch by 6Wunderkinder to Microsoft's acquisition and eventual shutdown in May 2020.",
    "author": {
      "@type": "Organization",
      "name": "Chill Study Work"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-01-01",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://chillstudy.work/wunderlist/history/"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "Wunderlist",
      "applicationCategory": "ProductivityApplication",
      "operatingSystem": "Windows, macOS, iOS, Android, Web"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Wunderlist History: The Beloved To-Do App That Microsoft Shut Down | CSW</title>
        <meta name="description" content="The complete history of Wunderlist, from its 2011 launch by 6Wunderkinder to Microsoft's acquisition and eventual shutdown in May 2020." />
        <meta name="keywords" content="Wunderlist history, Wunderlist shutdown, Microsoft To Do, 6Wunderkinder, task management app history, Christian Reber" />
        <link rel="canonical" href="https://chillstudy.work/wunderlist/history/" />
        <meta property="og:title" content="Wunderlist History: The Beloved To-Do App That Microsoft Shut Down" />
        <meta property="og:description" content="The complete history of Wunderlist, from its 2011 launch to Microsoft's acquisition and shutdown in 2020." />
        <meta property="og:url" content="https://chillstudy.work/wunderlist/history/" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Breadcrumb */}
          <nav className="mb-8 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-muted-foreground">
              <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
              <li>/</li>
              <li><Link to="/wunderlist/" className="hover:text-primary transition-colors">Wunderlist Alternative</Link></li>
              <li>/</li>
              <li className="text-foreground">History</li>
            </ol>
          </nav>

          {/* Hero Section */}
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Wunderlist: The Beloved To-Do App That Microsoft Shut Down
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              At the height of the productivity-app boom, Wunderlist stood out as a beautifully simple yet powerful to-do list. 
              Created by German startup <a href="https://en.wikipedia.org/wiki/6Wunderkinder" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">6Wunderkinder</a> in 2011, 
              the app let users organise tasks into lists, share them with friends or colleagues, set reminders and due dates, and sync across devices.
            </p>
          </header>

          {/* Timeline Section */}
          <section className="mb-16">
            <div className="space-y-8">
              {/* Origins */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold">Origins and Growth (2010-2015)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Founded in Berlin in 2010, 6Wunderkinder launched Wunderlist as a simple online task list that synced across devices. 
                  Users could create multiple lists (e.g., work, groceries, travel), add tasks with subtasks, notes and due dates, and share lists with collaborators.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Wunderlist's elegant interface and ease of use quickly amassed a devoted following. By 2015 the app boasted 
                  <strong className="text-foreground"> 13 million users</strong> and hosted over <strong className="text-foreground">1 billion to-do items</strong>. 
                  Investors took notice; 6Wunderkinder raised millions of dollars and was heralded as a European startup success story.
                </p>
              </article>

              {/* Microsoft Acquisition */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-accent/10 rounded-lg">
                    <Calendar className="w-6 h-6 text-accent" />
                  </div>
                  <h2 className="text-2xl font-bold">Microsoft's Acquisition (2015)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  In June 2015, <a href="https://news.microsoft.com/2015/06/02/microsoft-acquires-wunderlist/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Microsoft acquired 6Wunderkinder</a> for 
                  a rumoured $100 million. At the time, industry observers speculated that Microsoft wanted Wunderlist's expertise to bolster its productivity tools.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Two years after the acquisition, Microsoft announced plans to replace Wunderlist with a new app called 
                  <a href="https://todo.microsoft.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline"> Microsoft To Do</a>, 
                  built by the same team. Microsoft told users that To Do would eventually incorporate all of Wunderlist's features. 
                  Nonetheless, the announcement unsettled long-time users who feared losing the product they loved.
                </p>
              </article>

              {/* Why People Loved It */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-pink-500/10 rounded-lg">
                    <Heart className="w-6 h-6 text-pink-500" />
                  </div>
                  <h2 className="text-2xl font-bold">Why People Loved It</h2>
                </div>
                <div className="space-y-4 text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Simplicity with Power</h3>
                    <p>Wunderlist combined a clean interface with features like subtasks, reminders, recurring tasks and file attachments. Users could organise lists with folders and share them for collaborative projects, making Wunderlist suitable for everything from grocery lists to business planning.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Cross-Platform Sync</h3>
                    <p>Wunderlist worked on Windows, macOS, iOS, Android and the web. Real-time syncing meant tasks added on one device appeared instantly on others.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Collaboration</h3>
                    <p>Users could invite others to a list, assign tasks and discuss details via comments. This made Wunderlist popular among families and teams alike.</p>
                  </div>
                </div>
              </article>

              {/* The Shutdown */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-destructive/10 rounded-lg">
                    <XCircle className="w-6 h-6 text-destructive" />
                  </div>
                  <h2 className="text-2xl font-bold">The Shutdown (May 6, 2020)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  In December 2019, Microsoft announced that Wunderlist would be shut down on <strong className="text-foreground">May 6, 2020</strong>. 
                  The company explained that it wanted to focus its efforts on Microsoft To Do and would stop syncing Wunderlist tasks after the shutdown date.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Microsoft urged users to export their data or migrate to To Do. The news disappointed many loyal users; while Microsoft's new app offered 
                  similar functionality, it lacked some of Wunderlist's polish and features when first released.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  <a href="https://twitter.com/christianreber" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Christian Reber</a>, 
                  Wunderlist's founder, publicly offered to buy the app back from Microsoft so that he could keep it alive, but Microsoft declined and stuck to its plan. 
                  On May 6, 2020, the Wunderlist servers were turned off, ending a decade-long run for one of the most beloved productivity apps.
                </p>
              </article>

              {/* Community Reaction */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold">Community Reaction and Legacy</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Wunderlist's shutdown sparked an outpouring of nostalgia on social media. Fans posted screenshots of their long-running lists and 
                  lamented the end of an era. Critics accused Microsoft of buying the app only to kill it, while others acknowledged that maintaining 
                  two similar products was redundant.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Despite its demise, Wunderlist's influence lives on. Microsoft To Do gradually incorporated most of its predecessor's features. 
                  Former Wunderlist users also migrated to other task managers like{" "}
                  <a href="https://todoist.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Todoist</a>,{" "}
                  <a href="https://ticktick.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">TickTick</a>, and{" "}
                  <a href="https://www.any.do/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Any.do</a>.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Christian Reber went on to start <a href="https://www.superlist.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Superlist</a>, 
                  aiming to reimagine to-do and project management with a modern twist. For many, however, Wunderlist remains a benchmark for what a delightful to-do app can be.
                </p>
              </article>
            </div>
          </section>

          {/* Sources */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Sources</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://zenkit.com/en/blog/what-happened-to-wunderlist/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Zenkit's article</a> on 
                  What Happened to Wunderlist, noting 13 million users and the May 6, 2020 shutdown date.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://zapier.com/blog/wunderlist-shutting-down/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Zapier's report</a> confirming 
                  Microsoft's 2015 acquisition and the shutdown timeline.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://techcrunch.com/2017/04/19/microsoft-to-shut-down-wunderlist-in-favor-of-its-new-app-to-do/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">TechCrunch coverage</a> highlighting 
                  Microsoft's plan to retire Wunderlist in favour of Microsoft To Do.
                </span>
              </li>
            </ul>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 text-center border border-primary/20">
            <h2 className="text-2xl font-bold mb-4">Looking for a Wunderlist Alternative?</h2>
            <p className="text-muted-foreground mb-6">
              Discover how Chill Study Work combines task management with focused study sessions to help you stay productive.
            </p>
            <Link
              to="/wunderlist/"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
            >
              Explore CSW as Wunderlist Alternative
              <ArrowRight className="w-4 h-4" />
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WunderlistHistory;
